import * as actionConstants from "../constants/actionTypes";
import * as generalHelper from "../helpers/generalHelper";
import * as objectTypes from "../constants/objectTypes";
import * as propertyConstants from "../constants/propertyConstants";
import * as reducerHelper from "../helpers/reducerHelper";
import * as tableConstants from "../constants/tableConstants";

import { Package, updatePackage } from "../types/package";

export type PackageState = Readonly<{
    list: Array<Package>;
    activeList: Array<Package>;
    count: number | null;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    offset: number;
    page: number;
    systemList: Array<Package>;
    systemActiveList: Array<Package>;
    systemCountTotal: number | null;
    systemSearch: string | null;
    systemSearchParams: Record<propertyConstants.Property, string>;
    systemOffset: number;
    systemPage: number;
    zoneList: Array<Package>;
    zoneActiveList: Array<Package>;
    zoneCountTotal: number | null;
    zoneSearch: string | null;
    zoneSearchParams: Record<propertyConstants.Property, string>;
    zoneOffset: number;
    zonePage: number;
}>;

const initialState: PackageState = {
    list: [],
    activeList: [],
    count: null,
    sortingAsc: tableConstants.DEFAULT_SORTING_ASC,
    sortingCriterion: tableConstants.DEFAULT_SORTING_COLUMN,
    search: null,
    searchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    offset: tableConstants.DEFAULT_OFFSET,
    page: tableConstants.DEFAULT_PAGE,
    systemList: [],
    systemActiveList: [],
    systemCountTotal: null,
    systemSearch: null,
    systemSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    systemOffset: tableConstants.DEFAULT_OFFSET,
    systemPage: tableConstants.DEFAULT_PAGE,
    zoneList: [],
    zoneActiveList: [],
    zoneCountTotal: null,
    zoneSearch: null,
    zoneSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    zoneOffset: tableConstants.DEFAULT_OFFSET,
    zonePage: tableConstants.DEFAULT_PAGE
};

export default function packages(state: PackageState = initialState, action: Record<string, any>): PackageState {
    switch (action.type) {
        case actionConstants.LIST_SAVE: {
            switch (action.payload.objectType) {
                case objectTypes.PACKAGE: {
                    const newActiveList = generalHelper.updateActiveLists(state.activeList, action.payload.list);

                    return {
                        ...state,
                        list: action.payload.list,
                        activeList: newActiveList,
                        count: action.payload.totalCount
                    };
                }
                case objectTypes.SYSTEM_PACKAGE: {
                    const newActiveList = generalHelper.updateActiveLists(state.systemActiveList, action.payload.list);

                    return {
                        ...state,
                        systemList: action.payload.list,
                        systemActiveList: newActiveList,
                        systemCountTotal: action.payload.totalCount
                    };
                }
                case objectTypes.ZONE_PACKAGE: {
                    const newActiveList = generalHelper.updateActiveLists(state.zoneActiveList, action.payload.list);

                    return {
                        ...state,
                        zoneList: action.payload.list,
                        zoneActiveList: newActiveList,
                        zoneCountTotal: action.payload.totalCount
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_ACTIVE: {
            switch (action.payload.objectType) {
                case objectTypes.PACKAGE: {
                    return {
                        ...state,
                        activeList: action.payload.items
                    };
                }
                case objectTypes.SYSTEM_PACKAGE: {
                    return {
                        ...state,
                        systemActiveList: action.payload.items
                    };
                }
                case objectTypes.ZONE_PACKAGE: {
                    return {
                        ...state,
                        zoneActiveList: action.payload.items
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_OFFSET: {
            switch (action.payload.objectType) {
                case objectTypes.PACKAGE: {
                    return {
                        ...state,
                        offset: action.payload.offset,
                        page: action.payload.page
                    };
                }
                case objectTypes.SYSTEM_PACKAGE: {
                    return {
                        ...state,
                        systemOffset: action.payload.offset,
                        systemPage: action.payload.page
                    };
                }
                case objectTypes.ZONE_PACKAGE: {
                    return {
                        ...state,
                        zoneOffset: action.payload.offset,
                        zonePage: action.payload.page
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_SEARCH: {
            switch (action.payload.objectType) {
                case objectTypes.PACKAGE: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.SEARCH,
                        tableConstants.SEARCH_PARAMS
                    );
                }

                case objectTypes.SYSTEM_PACKAGE: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.SYSTEM_SEARCH,
                        tableConstants.SYSTEM_SEARCH_PARAMS
                    );
                }
                case objectTypes.ZONE_PACKAGE: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.ZONE_SEARCH,
                        tableConstants.ZONE_SEARCH_PARAMS
                    );
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_SORTING: {
            switch (action.payload.objectType) {
                case objectTypes.PACKAGE: {
                    return {
                        ...state,
                        sortingAsc: action.payload.asc,
                        sortingCriterion: action.payload.criterion
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.EDIT: {
            if (!action.payload.isModal) {
                switch (action.payload.objectType) {
                    case objectTypes.PACKAGE: {
                        const updatedPackageList = generalHelper.updateItem(
                            state.list,
                            action.payload.params[propertyConstants.PROPERTY_PACKAGE_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updatePackage
                        );

                        if (updatedPackageList.length) {
                            return {
                                ...state,
                                list: updatedPackageList
                            };
                        }

                        return state;
                    }
                    case objectTypes.SYSTEM_PACKAGE: {
                        const updatedPackageList = generalHelper.updateItem(
                            state.systemList,
                            action.payload.params[propertyConstants.PROPERTY_PACKAGE_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updatePackage
                        );

                        if (updatedPackageList.length) {
                            return {
                                ...state,
                                systemList: updatedPackageList
                            };
                        }

                        return state;
                    }
                    case objectTypes.ZONE_PACKAGE: {
                        const updatedPackageList = generalHelper.updateItem(
                            state.zoneList,
                            action.payload.params[propertyConstants.PROPERTY_PACKAGE_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updatePackage
                        );

                        if (updatedPackageList.length) {
                            return {
                                ...state,
                                zoneList: updatedPackageList
                            };
                        }

                        return state;
                    }
                    default: {
                        return state;
                    }
                }
            }
            return state;
        }
        case actionConstants.EDIT_SUCCESS: {
            if (!action.payload.isModal) {
                switch (action.payload.objectType) {
                    case objectTypes.PACKAGE: {
                        return {
                            ...state,
                            list: generalHelper.updateLists(state.list, action.payload.items),
                            activeList: generalHelper.updateActiveLists(state.activeList, action.payload.items)
                        };
                    }
                    case objectTypes.SYSTEM_PACKAGE: {
                        return {
                            ...state,
                            systemList: generalHelper.updateLists(state.systemList, action.payload.items)
                        };
                    }
                    case objectTypes.ZONE_PACKAGE: {
                        return {
                            ...state,
                            zoneList: generalHelper.updateLists(state.zoneList, action.payload.items)
                        };
                    }
                    default: {
                        return state;
                    }
                }
            }
            return state;
        }
        case actionConstants.NAVIGATION_SET_SCENE:
        case actionConstants.NAVIGATION_SET_WIZARD: {
            return initialState;
        }
        case actionConstants.EVENT_SAVE_DATABASES: {
            return initialState;
        }
        case actionConstants.DATA_ROLLBACK: {
            return initialState;
        }
        case actionConstants.SERVER_WEBSOCKET_CLOSED: {
            return initialState;
        }
        default: {
            return state;
        }
    }
}
